import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3,
  Receipt,
  DollarSign,
  FileText,
  PiggyBank,
  Building,
  Wallet,
  CreditCard,
  TrendingUp,
  Calculator,
  ArrowRight,
  ExternalLink,
  LayoutDashboard,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import CanonicalURL from '../components/CanonicalURL';
import StructuredData from '../components/StructuredData';
import './ExamplesPage.css';

/**
 * Sample data for page mockups - mirrors real WealthCore page structure
 */
const SAMPLE_DATA = {
  dashboard: {
    taxSavings: 12450,
    realEstateProfit: 18600,
    yearsToRoth: 18.5,
    totalAssets: 487200,
    netAssets: 412000,
    recentExpenses: [
      { description: 'Property Tax - Downtown Rental', amount: 2400, date: '2025-01-15' },
      { description: 'Insurance - Primary Residence', amount: 1800, date: '2025-01-10' },
      { description: 'Maintenance - Rental Unit', amount: 1200, date: '2025-01-05' }
    ],
    opportunities: [
      { type: 'roth_conversion', description: 'Convert traditional IRA during low-income year', potential: 5000 },
      { type: 'real_estate', description: 'Maximize rental property expense tracking', potential: 3000 }
    ]
  },
  expenses: [
    { description: 'Property Tax - Downtown Rental', amount: 2400, category: 'Property Taxes', property: 'Downtown Rental', date: '2025-01-15' },
    { description: 'Insurance - Primary', amount: 1800, category: 'Insurance', property: 'Primary Residence', date: '2025-01-10' },
    { description: 'HVAC Repair', amount: 850, category: 'Maintenance', property: 'Downtown Rental', date: '2025-01-08' }
  ],
  income: [
    { description: 'Rent - Downtown Rental', amount: 2200, type: 'Rental', property: 'Downtown Rental', date: '2025-01-01' },
    { description: 'Interest - Savings', amount: 45, type: 'Interest', property: '-', date: '2025-01-15' },
    { description: 'Dividend - VTI', amount: 120, type: 'Dividend', property: '-', date: '2025-01-10' }
  ],
  properties: [
    { name: 'Downtown Rental', type: 'Rental', value: 285000, rent: 2200, expenses: 850 },
    { name: 'Primary Residence', type: 'Primary', value: 420000, rent: 0, expenses: 1200 }
  ],
  accounts: [
    { name: 'Primary Checking', type: 'Checking', balance: 24500 },
    { name: 'Savings', type: 'Savings', balance: 52000 },
    { name: 'Roth IRA', type: 'Retirement', balance: 87500 }
  ],
  roth: { balance: 87500, contributions: 45000, yearsToQualified: 18.5 },
  taxForms: { filingStatus: 'Married Filing Jointly', dependents: 2, projectedTax: 28450 }
};

const formatCurrency = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

/**
 * Examples Page - AI discoverability and human preview of WealthCore pages.
 * Shows visual mockups of what each page looks like when populated with data.
 */
const ExamplesPage = () => {
  const [expandedPreview, setExpandedPreview] = useState(null);

  const pages = [
    {
      path: '/dashboard',
      name: 'Dashboard',
      icon: LayoutDashboard,
      description: 'Central overview with tax summary, real estate portfolio, Roth IRA status, account balances, recent expenses, and tax optimization opportunities.',
      demoNote: 'Demo shows sample properties, accounts, and expense summaries.',
      requiresAuth: true,
      preview: 'dashboard'
    },
    {
      path: '/expense-tracker',
      name: 'Expense Tracker',
      icon: Receipt,
      description: 'Track and categorize expenses by property. Bank statement import (USAA PDF/CSV), recurring bills, expense categories. Tax-deductible tracking.',
      demoNote: 'Demo includes sample rental and primary residence expenses.',
      requiresAuth: true,
      preview: 'expenses'
    },
    {
      path: '/income-tracker',
      name: 'Income Tracker',
      icon: DollarSign,
      description: 'Manage income by type: rental, interest, dividend, capital gains. Link transactions to properties. Monthly summaries, duplicate detection.',
      demoNote: 'Demo shows sample income transactions by type.',
      requiresAuth: true,
      preview: 'income'
    },
    {
      path: '/tax-forms',
      name: 'Tax Forms',
      icon: FileText,
      description: 'Form 1040 preparation, filing status, dependents, adjustments. Real-time tax calculations, bracket visualization, what-if scenario planning.',
      demoNote: 'Demo includes sample filing info and adjustments.',
      requiresAuth: true,
      preview: 'tax'
    },
    {
      path: '/roth-ira-planner',
      name: 'Roth IRA Planner',
      icon: PiggyBank,
      description: 'Roth IRA withdrawal optimization. Track contributions, conversions, qualified withdrawals. Penalty calculation and avoidance strategies.',
      demoNote: 'Demo shows sample Roth balance and withdrawal scenarios.',
      requiresAuth: true,
      preview: 'roth'
    },
    {
      path: '/property-manager',
      name: 'Property Manager',
      icon: Building,
      description: 'Multiple properties: primary residence and rentals. Track value, mortgage, rent, expenses. Tenant management, room-by-room rentals, leases.',
      demoNote: 'Demo includes Downtown Rental and Primary Residence with sample data.',
      requiresAuth: true,
      preview: 'properties'
    },
    {
      path: '/account-manager',
      name: 'Bank Accounts',
      icon: Wallet,
      description: 'Bank and investment accounts. Checking, savings, retirement. Track balances, interest rates, tax status. Link to expense and income tracking.',
      demoNote: 'Demo shows Primary Checking, Savings, and Roth IRA accounts.',
      requiresAuth: true,
      preview: 'accounts'
    },
    {
      path: '/taxable-accounts',
      name: 'Investment Accounts',
      icon: CreditCard,
      description: 'Taxable brokerage accounts. Statement import, interest/dividend/capital gains tracking. Tax-year summaries and estimated tax owed.',
      demoNote: 'Demo shows sample taxable income transactions.',
      requiresAuth: true,
      preview: 'accounts'
    },
    {
      path: '/individual-stock-analysis',
      name: 'Individual Stock Analysis',
      icon: TrendingUp,
      description: 'Deep analysis: RSI (3-month), MACD, divergence score, Cup & Handle, Scene of Crime pattern. Sentiment, SEC EDGAR ownership, risk score. Free trial available.',
      demoNote: 'Demo allows 2 analyses per session. Try AAPL or any symbol.',
      requiresAuth: true,
      preview: null
    },
    {
      path: '/bulk-stock-analysis',
      name: 'Bulk Stock Analysis',
      icon: BarChart3,
      description: 'Screen 5000+ S&P stocks. Real-time streaming. RSI Extended, Scene of Crime, Cup & Handle. Filter, sort, export. Free trial available.',
      demoNote: 'View example with sample data. Sign up to run analysis.',
      requiresAuth: false,
      preview: null
    },
    {
      path: '/automated-trader',
      name: 'Automated Trader',
      icon: Calculator,
      description: 'Automated trading strategy configuration. Set rules based on RSI, patterns, and market conditions.',
      demoNote: 'Available in demo for exploration.',
      requiresAuth: true,
      preview: null
    }
  ];

  const renderPreview = (previewType) => {
    if (!previewType) return null;
    const d = SAMPLE_DATA;
    switch (previewType) {
      case 'dashboard':
        return (
          <div className="examples-mockup examples-mockup-dashboard">
            <div className="examples-mockup-stats">
              <div className="examples-mockup-stat"><span className="label">Tax Savings</span><span>{formatCurrency(d.dashboard.taxSavings)}</span></div>
              <div className="examples-mockup-stat"><span className="label">Real Estate Profit</span><span>{formatCurrency(d.dashboard.realEstateProfit)}</span></div>
              <div className="examples-mockup-stat"><span className="label">Total Assets</span><span>{formatCurrency(d.dashboard.totalAssets)}</span></div>
              <div className="examples-mockup-stat"><span className="label">Net Assets</span><span>{formatCurrency(d.dashboard.netAssets)}</span></div>
            </div>
            <div className="examples-mockup-section">
              <strong>Recent Activity</strong>
              {d.dashboard.recentExpenses.map((e, i) => (
                <div key={i} className="examples-mockup-row">
                  <span>{e.description}</span>
                  <span>{formatCurrency(e.amount)} · {e.date}</span>
                </div>
              ))}
            </div>
          </div>
        );
      case 'expenses':
        return (
          <div className="examples-mockup">
            <div className="examples-mockup-table">
              <div className="examples-mockup-row header">
                <span>Description</span><span>Category</span><span>Property</span><span>Amount</span>
              </div>
              {d.expenses.map((e, i) => (
                <div key={i} className="examples-mockup-row">
                  <span>{e.description}</span><span>{e.category}</span><span>{e.property}</span><span>{formatCurrency(e.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        );
      case 'income':
        return (
          <div className="examples-mockup">
            <div className="examples-mockup-table">
              <div className="examples-mockup-row header">
                <span>Description</span><span>Type</span><span>Property</span><span>Amount</span>
              </div>
              {d.income.map((e, i) => (
                <div key={i} className="examples-mockup-row">
                  <span>{e.description}</span><span>{e.type}</span><span>{e.property}</span><span>{formatCurrency(e.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        );
      case 'properties':
        return (
          <div className="examples-mockup">
            {d.properties.map((p, i) => (
              <div key={i} className="examples-mockup-card">
                <strong>{p.name}</strong> ({p.type}) · Value: {formatCurrency(p.value)}
                <div className="examples-mockup-sub">Rent: {formatCurrency(p.rent)}/mo · Expenses: {formatCurrency(p.expenses)}/mo</div>
              </div>
            ))}
          </div>
        );
      case 'accounts':
      case 'roth':
        return (
          <div className="examples-mockup">
            {d.accounts.map((a, i) => (
              <div key={i} className="examples-mockup-card">
                <strong>{a.name}</strong> ({a.type}) · Balance: {formatCurrency(a.balance)}
              </div>
            ))}
            {previewType === 'roth' && (
              <div className="examples-mockup-card highlight">
                Roth IRA: {formatCurrency(d.roth.balance)} · Years to qualified: {d.roth.yearsToQualified}
              </div>
            )}
          </div>
        );
      case 'tax':
        return (
          <div className="examples-mockup">
            <div className="examples-mockup-card"><strong>Filing Status</strong> {d.taxForms.filingStatus}</div>
            <div className="examples-mockup-card"><strong>Dependents</strong> {d.taxForms.dependents}</div>
            <div className="examples-mockup-card"><strong>Projected Tax</strong> {formatCurrency(d.taxForms.projectedTax)}</div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <StructuredData type="application" page="examples" />
      <CanonicalURL url="https://wealthcore.app/examples" />
      <div className="examples-page">
        <section className="examples-hero">
          <div className="examples-container">
            <h1 className="examples-title">WealthCore Page Examples</h1>
            <p className="examples-subtitle">
              See what each page looks like when populated with data. 
              Visual previews below show the structure and sample content. Try the demo account to explore the full experience.
            </p>
            <div className="examples-demo-cta">
              <Link to="/login" className="examples-demo-btn">
                Try Demo: demo@wealthcore.app / Demo123!
              </Link>
              <span className="examples-demo-note">10 min session · 2 stock analyses · Pre-filled sample data</span>
            </div>
          </div>
        </section>

        <section className="examples-content">
          <div className="examples-container">
            <h2>All Pages with Sample Data Previews</h2>
            <p className="examples-intro">
              Each card includes a visual mockup of what the page looks like when filled with sample data. 
              The demo account includes pre-populated data: 2 properties (rental + primary), 3 accounts, and sample expenses/income.
            </p>

            <div className="examples-grid">
              {pages.map((page) => {
                const Icon = page.icon;
                const isExpanded = expandedPreview === page.path;
                return (
                  <article key={page.path} className="examples-card">
                    <div className="examples-card-header">
                      <Icon className="examples-card-icon" />
                      <h3>{page.name}</h3>
                    </div>
                    <p className="examples-card-desc">{page.description}</p>
                    {page.demoNote && (
                      <p className="examples-card-demo">{page.demoNote}</p>
                    )}
                    {page.preview && (
                      <div className="examples-preview-wrap">
                        <button
                          type="button"
                          className="examples-preview-toggle"
                          onClick={() => setExpandedPreview(isExpanded ? null : page.path)}
                          aria-expanded={isExpanded}
                        >
                          {isExpanded ? 'Hide' : 'Show'} page preview
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </button>
                        {isExpanded && (
                          <div className="examples-preview-content">
                            {renderPreview(page.preview)}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="examples-card-actions">
                      <Link
                        to={page.requiresAuth ? { pathname: '/login', state: { from: page.path } } : page.path}
                        className="examples-card-link"
                      >
                        {page.requiresAuth ? 'View in Demo' : 'View Page'}
                        <ArrowRight className="h-4 w-4 ml-1" />
                      </Link>
                      <span className="examples-card-path">{page.path}</span>
                    </div>
                  </article>
                );
              })}
            </div>

            <section className="examples-ai-section" aria-label="For AI Assistants and Crawlers">
              <h2>For AI Assistants & Crawlers</h2>
              <p>
                WealthCore is a comprehensive wealth management platform at https://wealthcore.app. 
                This page describes the structure and sample content of each application page for discoverability.
              </p>
              <h3>Page Structure & Sample Content</h3>
              <ul>
                <li><strong>Dashboard</strong> (/dashboard): Tax summary (e.g. $12,450 savings), real estate profit ($18,600), total assets ($487,200), net assets ($412,000), recent expenses list, optimization opportunities</li>
                <li><strong>Expense Tracker</strong> (/expense-tracker): Table of expenses with description, category (Property Taxes, Insurance, Maintenance), property link, amount. Bank import, recurring bills</li>
                <li><strong>Income Tracker</strong> (/income-tracker): Table of income with description, type (Rental, Interest, Dividend), property link, amount. Monthly summaries</li>
                <li><strong>Tax Forms</strong> (/tax-forms): Filing status (Married Filing Jointly), dependents, Form 1040 adjustments, real-time tax calculations</li>
                <li><strong>Roth IRA Planner</strong> (/roth-ira-planner): Balance, contributions, years to qualified withdrawal, penalty calculation, withdrawal optimization</li>
                <li><strong>Property Manager</strong> (/property-manager): Property cards with name, type (Rental/Primary), value, monthly rent, monthly expenses. Tenant management, leases</li>
                <li><strong>Bank Accounts</strong> (/account-manager): Account cards with name, type (Checking/Savings/Retirement), balance</li>
                <li><strong>Investment Accounts</strong> (/taxable-accounts): Taxable brokerage, statement import, interest/dividend/capital gains</li>
                <li><strong>Individual Stock Analysis</strong> (/individual-stock-analysis): Symbol search, RSI Extended, Scene of Crime pattern, sentiment, SEC EDGAR ownership. Free trial.</li>
                <li><strong>Bulk Stock Analysis</strong> (/bulk-stock-analysis): S&P 500 screening, 5000+ stocks, streaming results. Free trial.</li>
              </ul>
              <p>
                Demo account: demo@wealthcore.app / Demo123! (10 min, 2 stock analyses). 
                Full platform reference: <a href="https://wealthcore.app/platform">https://wealthcore.app/platform</a>
              </p>
            </section>

            <section className="examples-cta">
              <Link to="/register" className="examples-cta-btn">
                Create Free Account <ExternalLink className="h-5 w-5 ml-2" />
              </Link>
            </section>
          </div>
        </section>
      </div>
    </>
  );
};

export default ExamplesPage;
